import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { LocationForm } from "./LocationForm";
import { ShippingForm } from "./ShippingForm";
import { ThemeSettings } from "./ThemeSettings";
import { ProductFormModal } from "../../components/admin/ProductFormModal";
import { useStore } from "../../context/StoreContext";
import { api } from "../../services/api";
import { FaCheck, FaArrowRight, FaArrowLeft, FaTimes } from "react-icons/fa";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { HomePageZoneEditor } from "../../components/admin/HomePageZoneEditor";
import { PolicyPagesEditor } from "../../components/admin/PolicyPagesEditor";

interface WizardStep {
  key: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  required: boolean;
}

export const SetupWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStore();
  const [showWelcome, setShowWelcome] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null
  );
  const [editingShippingId, setEditingShippingId] = useState<string | null>(
    null
  );
  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    undefined
  );
  const [locations, setLocations] = useState<any[]>([]);
  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    fetchProgress(true, true); // Show loading on initial load
    fetchLocations();
    fetchShippingMethods();
    fetchProducts();
  }, []);

  const fetchProgress = async (
    autoNavigate: boolean = true,
    showLoading: boolean = false
  ) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await api.get("/store/setup-progress");
      setProgress(response.data);

      // If there's any progress, automatically skip the welcome page
      if (response.data.overallProgress > 0) {
        setShowWelcome(false);
        // Auto-navigate to first incomplete step only if autoNavigate is true
        if (autoNavigate) {
          const incompleteStep = response.data.items.findIndex(
            (item: any) => !item.completed
          );
          if (incompleteStep >= 0) {
            setCurrentStep(incompleteStep);
          } else {
            // All steps completed, show last step
            setCurrentStep(response.data.items.length - 1);
          }
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch progress:", err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get("/locations/admin");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      console.error("Failed to fetch locations:", err);
    }
  };

  const fetchShippingMethods = async () => {
    try {
      const response = await api.get("/shipping/admin");
      setShippingMethods(response.data.methods || []);
    } catch (err: any) {
      console.error("Failed to fetch shipping methods:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products?limit=10&status=");
      setProducts(response.data.products || []);
    } catch (err: any) {
      console.error("Failed to fetch products:", err);
    }
  };

  const handleLocationSuccess = () => {
    setShowLocationForm(false);
    setEditingLocationId(null);
    fetchLocations();
    fetchProgress(false, false); // Don't show loading or auto-navigate
  };

  const handleShippingSuccess = () => {
    setShowShippingForm(false);
    setEditingShippingId(null);
    fetchShippingMethods();
    fetchProgress(false, false); // Don't show loading or auto-navigate
  };

  const handleProductSuccess = () => {
    setShowProductModal(false);
    setEditingProductId(undefined);
    fetchProducts();
    fetchProgress(false, false); // Don't show loading or auto-navigate
  };

  const handleThemeSave = () => {
    // Optimistically update progress and navigate immediately for better UX
    // Refresh progress in background without blocking navigation or showing loading
    fetchProgress(false, false).catch(() => {
      // Silently fail - progress will refresh on next interaction
    });

    // Navigate immediately for better UX
    setCurrentStep((prevStep) => {
      if (prevStep < steps.length - 1) {
        // Use requestAnimationFrame to ensure smooth transition
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
        return prevStep + 1;
      }
      return prevStep;
    });
  };

  const handleGetStarted = () => {
    setShowWelcome(false);
    // Navigate to first incomplete step if available
    if (progress?.items) {
      const incompleteStep = progress.items.findIndex(
        (item: any) => !item.completed
      );
      if (incompleteStep >= 0) {
        setCurrentStep(incompleteStep);
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleComplete = () => {
    navigate("/admin/dashboard");
  };

  const handleStepClick = (index: number) => {
    setCurrentStep(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Define wizard steps
  const steps: WizardStep[] = [
    {
      key: "locations",
      title: t("admin.setupWizard.steps.locations.title"),
      description: t("admin.setupWizard.steps.locations.description"),
      component: () => (
        <div className="space-y-4">
          {locations.length > 0 ? (
            <div className="space-y-2">
              {locations.map((location) => (
                <Card key={location._id} padding="sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{location.country}</div>
                      <div className="text-sm text-gray-500">
                        {location.currency} ({location.currencySymbol}) -{" "}
                        {t("admin.setupWizard.steps.locations.taxLabel")}:{" "}
                        {location.taxRate}%
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingLocationId(location._id);
                        setShowLocationForm(true);
                      }}
                    >
                      {t("common.edit")}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Alert variant="info">
              {t("admin.setupWizard.steps.locations.noLocations")}
            </Alert>
          )}
          <Button
            onClick={() => {
              setEditingLocationId(null);
              setShowLocationForm(true);
            }}
          >
            {locations.length > 0
              ? t("admin.setupWizard.steps.locations.addAnother")
              : t("admin.setupWizard.steps.locations.addFirst")}
          </Button>
        </div>
      ),
      required: true,
    },
    {
      key: "shipping",
      title: t("admin.setupWizard.steps.shipping.title"),
      description: t("admin.setupWizard.steps.shipping.description"),
      component: () => (
        <div className="space-y-4">
          {shippingMethods.length > 0 ? (
            <div className="space-y-2">
              {shippingMethods.map((method) => (
                <Card key={method._id} padding="sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{method.name}</div>
                      <div className="text-sm text-gray-500">
                        {(() => {
                          const typeLabels: Record<string, string> = {
                            standard: t("admin.shipping.typeStandard"),
                            express: t("admin.shipping.typeExpress"),
                            pickup: t("admin.shipping.typePickup"),
                            digital: t("admin.shipping.typeDigital"),
                          };
                          return typeLabels[method.type] || method.type;
                        })()}{" "}
                        - {method.basePrice}{" "}
                        {method.currency ||
                          t("admin.setupWizard.steps.shipping.defaultCurrency")}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingShippingId(method._id);
                        setShowShippingForm(true);
                      }}
                    >
                      {t("common.edit")}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Alert variant="info">
              {t("admin.setupWizard.steps.shipping.noMethods")}
            </Alert>
          )}
          <Button
            onClick={() => {
              setEditingShippingId(null);
              setShowShippingForm(true);
            }}
          >
            {shippingMethods.length > 0
              ? t("admin.setupWizard.steps.shipping.addAnother")
              : t("admin.setupWizard.steps.shipping.addFirst")}
          </Button>
        </div>
      ),
      required: true,
    },
    {
      key: "currencies",
      title: t("admin.setupWizard.steps.currencies.title"),
      description: t("admin.setupWizard.steps.currencies.description"),
      component: () => (
        <div className="space-y-4">
          <Alert variant="info">
            {t("admin.setupWizard.steps.currencies.info")}
          </Alert>
          {locations.length > 0 && (
            <div className="space-y-2">
              {locations.map((location) => (
                <Card key={location._id} padding="sm">
                  <div className="font-medium">{location.country}</div>
                  <div className="text-sm text-gray-500">
                    {t("admin.setupWizard.steps.currencies.currencyLabel")}:{" "}
                    {location.currency} ({location.currencySymbol})
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      ),
      required: true,
    },
    {
      key: "theme",
      title: t("admin.setupWizard.steps.theme.title"),
      description: t("admin.setupWizard.steps.theme.description"),
      component: () => (
        <ThemeSettings
          onSave={handleThemeSave}
          showTopSaveButton={true}
          stepTitle={t("admin.setupWizard.steps.theme.title")}
        />
      ),
      required: false,
    },
    {
      key: "zones",
      title: t("admin.setupWizard.steps.zones.title"),
      description: t("admin.setupWizard.steps.zones.description"),
      component: () => (
        <div className="space-y-4">
          <HomePageZoneEditor onSuccess={() => fetchProgress(false, false)} />
        </div>
      ),
      required: false,
    },
    {
      key: "catalog",
      title: t("admin.setupWizard.steps.catalog.title"),
      description: t("admin.setupWizard.steps.catalog.description"),
      component: () => (
        <div className="space-y-4">
          {products.length > 0 ? (
            <div className="space-y-2">
              <div className="text-sm text-gray-600 mb-2">
                {t("admin.setupWizard.steps.catalog.existingProducts", {
                  count: products.length,
                })}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {products.slice(0, 6).map((product) => (
                  <Card key={product._id} padding="sm">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {product.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {(() => {
                            const statusLabels: Record<string, string> = {
                              active: t("admin.active"),
                              draft: t("admin.draft"),
                              archived: t("admin.archived"),
                            };
                            return (
                              statusLabels[product.status] || product.status
                            );
                          })()}{" "}
                          - {product.currencySymbol || "$"}
                          {product.basePrice || 0}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingProductId(product._id);
                          setShowProductModal(true);
                        }}
                      >
                        {t("common.edit")}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Alert variant="info">
              {t("admin.setupWizard.steps.catalog.noProducts")}
            </Alert>
          )}
          <Button
            onClick={() => {
              setEditingProductId(undefined);
              setShowProductModal(true);
            }}
          >
            {products.length > 0
              ? t("admin.setupWizard.steps.catalog.addAnother")
              : t("admin.setupWizard.steps.catalog.addFirst")}
          </Button>
        </div>
      ),
      required: true,
    },
    {
      key: "policies",
      title: t("admin.setupWizard.steps.policies.title"),
      description: t("admin.setupWizard.steps.policies.description"),
      component: () => (
        <div className="space-y-4">
          <PolicyPagesEditor
            onSuccess={() => {
              fetchProgress(false, false);
            }}
          />
        </div>
      ),
      required: false,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show welcome page
  if (showWelcome) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin/dashboard")}
                  className="flex items-center gap-2"
                >
                  <FaTimes />
                  {t("common.close")}
                </Button>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">
                    {t("admin.setupWizard.title")}
                  </h1>
                </div>
              </div>
              <LanguageSwitcher />
            </div>
          </div>
        </header>

        {/* Welcome Content */}
        <main className="flex-1 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-3xl w-full">
            <Card padding="lg">
              <div className="text-center space-y-4">
                <div className="space-y-2">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    {t("admin.setupWizard.welcome.title")}
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-600">
                    {t("admin.setupWizard.welcome.subtitle")}
                  </p>
                  <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto">
                    {t("admin.setupWizard.welcome.description")}
                  </p>
                </div>

                {/* Store Information Section */}
                {store && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-left">
                      <h3 className="text-base font-semibold text-gray-900 mb-3">
                        {t("admin.setupWizard.welcome.storeInfo.title")}
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm font-medium text-gray-600">
                            {t("admin.setupWizard.welcome.storeInfo.storeName")}
                            :
                          </span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">
                            {store.name}
                          </span>
                        </div>
                        {store.subdomain && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm font-medium text-gray-600">
                              {t(
                                "admin.setupWizard.welcome.storeInfo.subdomain"
                              )}
                              :
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-900">
                              {store.subdomain}
                            </span>
                          </div>
                        )}
                        {store.domain && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm font-medium text-gray-600">
                              {t("admin.setupWizard.welcome.storeInfo.domain")}:
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-900">
                              {store.domain}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm font-medium text-gray-600">
                            {t(
                              "admin.setupWizard.welcome.storeInfo.businessModel"
                            )}
                            :
                          </span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">
                            {store.businessModel === "plugin-based"
                              ? t(
                                  "admin.setupWizard.welcome.storeInfo.businessModelPlugin"
                                )
                              : t(
                                  "admin.setupWizard.welcome.storeInfo.businessModelPayAsYouGo"
                                )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4">
                  <div className="text-left space-y-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      {t("admin.setupWizard.welcome.features.title")}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.locations")}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.shipping")}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.payout")}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.theme")}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.catalog")}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                          <FaCheck className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">
                          {t("admin.setupWizard.welcome.features.policies")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={handleGetStarted} size="lg">
                    <span className="flex items-center">
                      {t("admin.setupWizard.welcome.getStarted")}
                      <FaArrowRight className="ml-2" />
                    </span>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  const CurrentStepComponent = steps[currentStep]?.component;
  const overallProgress = progress?.overallProgress || 0;

  return (
    <div className="setup-wizard min-h-screen bg-gray-50 flex flex-col">
      {/* Header with Progress Indicator */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="flex items-center gap-2"
              >
                <FaTimes />
                {t("common.close")}
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {t("admin.setupWizard.title")}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Progress Indicator */}
              {overallProgress < 100 && (
                <div className="flex items-center gap-3">
                  <div className="relative w-12 h-12">
                    <svg className="w-12 h-12 transform -rotate-90">
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                        className="text-gray-200"
                      />
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 20}`}
                        strokeDashoffset={`${2 * Math.PI * 20 * (1 - overallProgress / 100)}`}
                        className="text-blue-600 transition-all duration-300"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-700">
                        {overallProgress}%
                      </span>
                    </div>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">
                      {progress?.completedItems || 0} /{" "}
                      {progress?.totalItems || 0}{" "}
                      {t("admin.setupProgress.completed")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Steps Navigation */}
        <aside className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 space-y-2">
            {steps.map((step, index) => {
              const stepProgress = progress?.items?.find(
                (item: any) => item.key === step.key
              );
              const isCompleted = stepProgress?.completed || false;
              const isCurrent = index === currentStep;

              return (
                <button
                  key={step.key}
                  onClick={() => handleStepClick(index)}
                  className={`w-full text-left p-3 rounded-lg transition-all duration-300 ease-in-out transform ${
                    isCurrent
                      ? "bg-blue-100 border-2 border-blue-500 scale-105 shadow-md"
                      : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent hover:scale-[1.02]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ease-in-out ${
                        isCompleted
                          ? "bg-green-500 text-white"
                          : isCurrent
                            ? "bg-blue-500 text-white"
                            : "bg-gray-300 text-gray-600"
                      }`}
                    >
                      {isCompleted ? (
                        <FaCheck className="text-xs" />
                      ) : (
                        <span className="text-xs font-bold">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isCurrent ? "text-blue-900" : "text-gray-700"
                        }`}
                      >
                        {step.title}
                      </div>
                      {step.required && !isCompleted && (
                        <div className="text-xs text-red-600 mt-0.5">
                          {t("admin.setupWizard.required")}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6">
            <Card padding="lg">
              <div className="space-y-6 pb-20">
                {/* Step Header */}
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {steps[currentStep]?.title}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {steps[currentStep]?.description}
                  </p>
                </div>

                {/* Step Content */}
                <div>{CurrentStepComponent && <CurrentStepComponent />}</div>
              </div>
            </Card>
          </div>
        </main>
      </div>

      {/* Sticky Navigation Footer */}
      <footer className="bg-white border-t border-gray-200 sticky bottom-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              {currentStep > 0 ? (
                <Button variant="outline" onClick={handlePrevious}>
                  <FaArrowLeft className="mr-2" />
                  {t("common.previous")}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setShowWelcome(true)}>
                  <FaArrowLeft className="mr-2" />
                  {t("common.back")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {/* All steps can be skipped */}
              <Button variant="outline" onClick={handleSkip}>
                {t("common.skip")}
              </Button>
              {currentStep < steps.length - 1 ? (
                <Button onClick={handleNext}>
                  {t("common.next")}
                  <FaArrowRight className="ml-2" />
                </Button>
              ) : (
                <Button onClick={handleComplete}>
                  {t("admin.setupWizard.complete")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <LocationForm
        isOpen={showLocationForm}
        locationId={editingLocationId}
        onClose={() => {
          setShowLocationForm(false);
          setEditingLocationId(null);
        }}
        onSuccess={handleLocationSuccess}
      />

      <ShippingForm
        isOpen={showShippingForm}
        methodId={editingShippingId}
        onClose={() => {
          setShowShippingForm(false);
          setEditingShippingId(null);
        }}
        onSuccess={handleShippingSuccess}
      />

      <ProductFormModal
        isOpen={showProductModal}
        productId={editingProductId}
        onClose={() => {
          setShowProductModal(false);
          setEditingProductId(undefined);
        }}
        onSuccess={handleProductSuccess}
      />
    </div>
  );
};
